import { memo } from 'react'
import { Marker, Popup } from 'react-leaflet'
import { Link } from 'react-router-dom'
import L from 'leaflet'
import { useTranslation } from 'react-i18next'
import { CalendarDaysIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'

const logger = createLogger('EventMarker')

const calendarSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="event-marker-icon">
  <path d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
  <path fill-rule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clip-rule="evenodd" />
</svg>`

// White glassmorphism pin — upcoming events (primary border)
const upcomingEventIcon = L.divIcon({
  className: '',
  html: `<div class="event-map-marker">${calendarSvg}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -38],
})

// White glassmorphism pin — live events (green border)
const liveEventIcon = L.divIcon({
  className: '',
  html: `<div class="event-map-marker event-map-marker--live">${calendarSvg}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -38],
})

/**
 * EventMarker - Leaflet marker for pop-up events on the map
 * Shows a calendar icon at the event's lat/lng; popup links to /events/:id
 * @atomic @idempotent
 */
const EventMarker = memo(function EventMarker({ event }) {
  const { t } = useTranslation()

  const lat = event.latitude
  const lng = event.longitude
  if (!lat || !lng) return null

  const start = new Date(event.startAt)
  const end = new Date(event.endAt)
  const now = Date.now()
  const isLive = start.getTime() <= now && end.getTime() >= now

  return (
    <Marker
      position={[lat, lng]}
      icon={isLive ? liveEventIcon : upcomingEventIcon}
      title={event.title}
      zIndexOffset={200}
    >
      <Popup>
        <div className="min-w-[210px] max-w-[240px]">
          {/* Status strip */}
          <div className={`h-1 -mx-3 -mt-3 mb-3 rounded-t-sm ${isLive ? 'bg-green-500' : 'bg-primary/50'}`} />

          <div className="space-y-2">
            {/* Status badge */}
            {isLive ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {t('events.status.live')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                {t('events.status.upcoming')}
              </span>
            )}

            {/* Title */}
            <p className="font-bold text-gray-900 text-sm leading-snug">{event.title}</p>

            {/* Date/time */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <CalendarDaysIcon className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" />
              <span>
                {start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {' · '}
                {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {'–'}
                {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>

            {/* Location */}
            {event.locationName && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <MapPinIcon className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" />
                <span className="truncate">{event.locationName}</span>
              </div>
            )}

            {/* Vendor count */}
            {event.participantCount > 0 && (
              <p className="text-xs text-gray-400">
                {t('map.events.participantCount', { count: event.participantCount })}
              </p>
            )}

            {/* CTA */}
            <Link
              to={`/events/${event.id}`}
              className="block w-full text-center text-xs py-1.5 px-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors font-semibold"
            >
              {t('map.events.viewDetails')}
            </Link>
          </div>
        </div>
      </Popup>
    </Marker>
  )
})

export default EventMarker
